import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import UserContext from '../../../../components/User_Context.js';
import * as menu from "../../../../assets/menus/menu.js";
import * as api from "../../../../api/apiConnector.js";
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Choices from '../../../../components/Choices.js';
import * as breadcrumb from "../../../../components/ui/breadcrumb.js";
import { formBuilderLabels } from '../../../_label/form_builder/form_builder_labels.js';

function Form_Assignment_Screen() {
    const { formId } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useContext(UserContext);
    const { t } = useTranslation();
    const MySwal = withReactContent(Swal);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(null);
    const [selectedUser, setSelectedUser] = useState("");
    const [users, setUsers] = useState([]);
    const [formList, setFormList] = useState([]);
    const [selectedForm, setSelectedForm] = useState(formId || "");
    const [requiresValidation, setRequiresValidation] = useState(false);
    const [selectedUserObject, setSelectedUserObject] = useState(null);
    const [expectedCompilationStartDate, setExpectedCompilationStartDate] = useState("");
    const [expectedCompilationEndDate, setExpectedCompilationEndDate] = useState("");
    const [cycles, setCycles] = useState(1);

    const [activeTab, setActiveTab] = useState('user'); // 'user' or 'group'
    const [selectedGroup, setSelectedGroup] = useState("");
    const [groups, setGroups] = useState([]);
    const [selectedGroupObject, setSelectedGroupObject] = useState(null);

    // Search assignments states
    const [searchLoading, setSearchLoading] = useState(false);
    const [assignments, setAssignments] = useState([]);
    const [searchFilters, setSearchFilters] = useState({
        includeInactive: false,
        userId: "",
        groupId: "",
        formId: "",
        startDateFrom: "",
        endDateBefore: "",
        requiresValidation: null
    });

    // Edit modal states
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingAssignment, setEditingAssignment] = useState(null);
    const [editForm, setEditForm] = useState({
        selectedUser: "",
        selectedGroup: "",
        selectedForm: "",
        requiresValidation: false,
        expectedCompilationStartDate: "",
        expectedCompilationEndDate: "",
        cycles: 1,
        activeTab: 'user'
    });

    useEffect(() => {
        (async () => {
            menu.write({ user: currentUser() });
        })();
    }, []);

    // Initialize translations from centralized file
    useEffect(() => {
        const i18n = menu.getI18n();
        i18n.addResourceBundle('it', 'labels', formBuilderLabels.it, true, true);
        i18n.addResourceBundle('en', 'labels', formBuilderLabels.en, true, true);
    }, []);

    // Set breadcrumb
    const setBreadCrumb = () => {
        const formName = form ? form.Name : t('form', { ns: 'labels' });
        
        const path = [
            { link: "/", name: t('home', { ns: 'labels' }) },
            { link: "/form_builder/finder", name: t('form', { ns: 'labels' }) },
            { link: `/form/assignments/${formId || ''}`, name: `${t('formAssignment', { ns: 'labels' })}: ${formName}` }
        ];
        
        breadcrumb.set(path);
    };

    // Load form data and users
    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);

                // Load users
                const usersResult = await api.sendMessage("admin/security/getAllUsers", {
                    user: currentUser(),
                    body: {}
                });

                if (usersResult?.success && usersResult?.users) {
                    // Transform users data to match Choices component format
                    const formattedUsers = usersResult.users.map(user => {
                        // Build display name with name, surname and email
                        const name = user.Name ||  '';
                        const surname = user.LastName || '';
                        const email = user.EMail ||  '';
                        
                        // Format display name: "Name Surname (email)"
                        let displayName = name;
                        if (surname) {
                            displayName += ` ${surname}`;
                        }
                        if (email) {
                            displayName += ` (${email})`;
                        }
                        
                        return {
                            Code: user.Id,
                            Name: displayName || email || user.Username || 'ANONYMOUS',
                            OriginalUser: user // Keep the original user object
                        };
                    });
                    setUsers(formattedUsers);
                }

                // Load groups
                const groupsResult = await api.sendMessage("admin/security/getGroups", {
                    user: currentUser(),
                    body: {}
                });

                if (groupsResult?.success && groupsResult?.groups) {
                    const formattedGroups = groupsResult.groups.map(group => ({
                        Code: group.Id,
                        Name: group.Name || 'Unnamed Group',
                        OriginalGroup: group
                    }));
                    setGroups(formattedGroups);
                }

                // Load forms for dropdown
                const formsResult = await api.sendMessage("darm/formBuilder/finder", {
                    user: currentUser(),
                    body: {}
                });

                if (formsResult?.filtered) {
                    // Transform form data to match Choices component format
                    const formattedForms = formsResult.filtered.map(form => ({
                        Code: form.Id,
                        Name: form.FormName
                    }));
                    setFormList(formattedForms);
                }

                // If formId is provided, load the specific form
                if (formId) {
                    const formResult = await api.sendMessage("darm/formBuilder/get", {
                        user: currentUser(),
                        body: { id: formId }
                    });

                    if (formResult?.success && formResult?.form) {
                        setForm(formResult.form);
                        setSelectedForm(formId);
                    }
                }
            } catch (error) {
                console.error("Error loading data:", error);
                MySwal.fire({
                    title: t('error', { ns: 'labels' }),
                    text: error.message || t('error', { ns: 'labels' }),
                    icon: 'error'
                });
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [formId, currentUser]);

    // Update breadcrumb when form changes
    useEffect(() => {
        setBreadCrumb();
    }, [form]);

    // Handle form assignment using new API
    const handleAssign = async () => {
        // Validate that at least one user or form is selected
        if ((activeTab === 'user' && !selectedUserObject) || (activeTab === 'group' && !selectedGroupObject) || !selectedForm) {
            MySwal.fire({
                title: t('error', { ns: 'labels' }),
                text: t(activeTab === 'user' ? 'userRequired' : 'groupRequired', { ns: 'labels' }),
                icon: 'error'
            });
            return;
        }

        try {
            setSaving(true);

            // Show loading modal
            MySwal.fire({
                title: t('saving', { ns: 'labels' }),
                text: t('saving', { ns: 'labels' }),
                allowOutsideClick: false,
                showConfirmButton: false,
                willOpen: () => {
                    Swal.showLoading();
                }
            });

            // Prepare assignment object according to new API documentation
            const assignment = {
                FormId: selectedForm,
                ToValidate: requiresValidation,
                Cycles: cycles || 1,
                Status: "Active"
            };

            // Set user or group (mutually exclusive)
            if (activeTab === 'user') {
                assignment.UserId = selectedUserObject.Id || selectedUserObject.id || selectedUserObject.ID;
            } else {
                assignment.GroupId = selectedGroupObject.Id;
            }

            // Add dates if provided (convert to ISO format)
            if (expectedCompilationStartDate) {
                assignment.StartDate = new Date(expectedCompilationStartDate).toISOString();
            }
            if (expectedCompilationEndDate) {
                assignment.EndDate = new Date(expectedCompilationEndDate).toISOString();
            }

            // Call the new API endpoint
            const result = await api.sendMessage("darm/formAssignments/save", {
                user: currentUser(),
                body: { assignment }
            });

            if (result?.success) {
                // Show success message
                MySwal.fire({
                    title: t('success', { ns: 'labels' }),
                    text: t('assignSuccess', { ns: 'labels' }),
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });

                // Reset fields to allow for another assignment, keeping the form selected.
                setSelectedUser("");
                setSelectedUserObject(null);
                setSelectedGroup("");
                setSelectedGroupObject(null);
                setRequiresValidation(false);
                setExpectedCompilationStartDate("");
                setExpectedCompilationEndDate("");
                setCycles(1);
            } else {
                throw new Error(result?.error || t('assignError', { ns: 'labels' }));
            }
        } catch (error) {
            console.error("Error assigning form:", error);
            MySwal.fire({
                title: t('error', { ns: 'labels' }),
                text: error.message || t('assignError', { ns: 'labels' }),
                icon: 'error'
            });
        } finally {
            setSaving(false);
        }
    };



    // Search assignments function
    const searchAssignments = async (filters = searchFilters) => {
        try {
            setSearchLoading(true);

            // Build request body with only non-empty filters
            const requestBody = {};
            
            if (filters.includeInactive) {
                requestBody.includeInactive = filters.includeInactive;
            }
            if (filters.userId) {
                requestBody.userId = filters.userId;
            }
            if (filters.groupId) {
                requestBody.groupId = filters.groupId;
            }
            if (filters.formId) {
                requestBody.formId = filters.formId;
            }
            if (filters.startDateFrom) {
                requestBody.startDateFrom = filters.startDateFrom;
            }
            if (filters.endDateBefore) {
                requestBody.endDateBefore = filters.endDateBefore;
            }
            if (filters.requiresValidation !== null) {
                requestBody.requiresValidation = filters.requiresValidation;
            }

            const result = await api.sendMessage("darm/formAssignments/getFiltered", {
                user: currentUser(),
                body: requestBody
            });

            if (result?.success) {
                setAssignments(result.assignments || []);
            } else {
                throw new Error(result?.error || t('searchError', { ns: 'labels' }));
            }
        } catch (error) {
            console.error("Error searching assignments:", error);
            MySwal.fire({
                title: t('error', { ns: 'labels' }),
                text: error.message || t('searchError', { ns: 'labels' }),
                icon: 'error'
            });
        } finally {
            setSearchLoading(false);
        }
    };

    // Handle search filter changes
    const handleFilterChange = (filterName, value) => {
        const newFilters = { ...searchFilters, [filterName]: value };
        
        // Validazione date: se si imposta endDateBefore, deve essere dopo startDateFrom
        if (filterName === 'endDateBefore' && value && newFilters.startDateFrom) {
            const startDate = new Date(newFilters.startDateFrom);
            const endDate = new Date(value);
            
            if (endDate <= startDate) {
                MySwal.fire({
                    title: t('error', { ns: 'labels' }),
                    text: t('endDateMustBeAfterStartDate', { ns: 'labels' }) || 'La data fine deve essere successiva alla data inizio',
                    icon: 'error'
                });
                return; // Non aggiorna il filtro se la validazione fallisce
            }
        }
        
        // Validazione date: se si imposta startDateFrom e esiste endDateBefore, controlla coerenza
        if (filterName === 'startDateFrom' && value && newFilters.endDateBefore) {
            const startDate = new Date(value);
            const endDate = new Date(newFilters.endDateBefore);
            
            if (endDate <= startDate) {
                MySwal.fire({
                    title: t('error', { ns: 'labels' }),
                    text: t('startDateMustBeBeforeEndDate', { ns: 'labels' }) || 'La data inizio deve essere precedente alla data fine',
                    icon: 'error'
                });
                return; // Non aggiorna il filtro se la validazione fallisce
            }
        }
        
        setSearchFilters(newFilters);
    };

    // Handle clear filters
    const handleClearFilters = () => {
        const clearedFilters = {
            includeInactive: false,
            userId: "",
            groupId: "",
            formId: "",
            startDateFrom: "",
            endDateBefore: "",
            requiresValidation: null
        };
        setSearchFilters(clearedFilters);
        setAssignments([]);
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            return new Date(dateString).toLocaleDateString('it-IT');
        } catch (error) {
            return dateString;
        }
    };

    // Get user name from ID
    const getUserName = (userId) => {
        if (!userId) return '-';
        const user = users.find(u => u.Code === userId);
        return user ? user.Name : userId;
    };

    // Get group name from ID
    const getGroupName = (groupId) => {
        if (!groupId) return '-';
        const group = groups.find(g => g.Code === groupId);
        return group ? group.Name : groupId;
    };

    // Get form name from ID
    const getFormName = (formId) => {
        if (!formId) return '-';
        const form = formList.find(f => f.Code === formId);
        return form ? form.Name : formId;
    };

    // Open edit modal with assignment data
    const openEditModal = (assignment) => {
        setEditingAssignment(assignment);
        
        // Find the user or group objects
        const userObj = assignment.UserId ? users.find(u => u.Code === assignment.UserId) : null;
        const groupObj = assignment.GroupId ? groups.find(g => g.Code === assignment.GroupId) : null;
        
        setEditForm({
            selectedUser: assignment.UserId || "",
            selectedGroup: assignment.GroupId || "",
            selectedForm: assignment.FormId || "",
            requiresValidation: assignment.ToValidate || false,
            expectedCompilationStartDate: assignment.StartDate ? assignment.StartDate.split('T')[0] : "",
            expectedCompilationEndDate: assignment.EndDate ? assignment.EndDate.split('T')[0] : "",
            cycles: assignment.Cycles || 1,
            activeTab: assignment.UserId ? 'user' : 'group'
        });
        
        setShowEditModal(true);
    };

    // Close edit modal
    const closeEditModal = () => {
        setShowEditModal(false);
        setEditingAssignment(null);
        setEditForm({
            selectedUser: "",
            selectedGroup: "",
            selectedForm: "",
            requiresValidation: false,
            expectedCompilationStartDate: "",
            expectedCompilationEndDate: "",
            cycles: 1,
            activeTab: 'user'
        });
    };

    // Save assignment changes
    const handleSaveChanges = async () => {
        // Show confirmation dialog
        const confirmResult = await MySwal.fire({
            title: t('confirmSave', { ns: 'labels' }) || 'Conferma salvataggio',
            text: t('areYouSureToSave', { ns: 'labels' }) || 'Sei sicuro di voler salvare le modifiche?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#6c757d',
            confirmButtonText: t('yes', { ns: 'labels' }) || 'Sì',
            cancelButtonText: t('cancel', { ns: 'labels' }) || 'Annulla'
        });

        if (!confirmResult.isConfirmed) {
            return;
        }

        try {
            setSaving(true);

            // Show loading modal
            MySwal.fire({
                title: t('saving', { ns: 'labels' }),
                text: t('saving', { ns: 'labels' }),
                allowOutsideClick: false,
                showConfirmButton: false,
                willOpen: () => {
                    Swal.showLoading();
                }
            });

            // Prepare assignment object
            const assignment = {
                Id: editingAssignment.Id, // Include ID for update
                FormId: editForm.selectedForm,
                ToValidate: editForm.requiresValidation,
                Cycles: editForm.cycles || 1,
                Status: editingAssignment.Status // Keep original status
            };

            // Set user or group (mutually exclusive)
            if (editForm.activeTab === 'user') {
                assignment.UserId = editForm.selectedUser;
                assignment.GroupId = null; // Clear group when user is selected
            } else {
                assignment.GroupId = editForm.selectedGroup;
                assignment.UserId = null; // Clear user when group is selected
            }

            // Add dates if provided (convert to ISO format)
            if (editForm.expectedCompilationStartDate) {
                assignment.StartDate = new Date(editForm.expectedCompilationStartDate).toISOString();
            }
            if (editForm.expectedCompilationEndDate) {
                assignment.EndDate = new Date(editForm.expectedCompilationEndDate).toISOString();
            }

            // Call the save API endpoint
            const result = await api.sendMessage("darm/formAssignments/save", {
                user: currentUser(),
                body: { assignment }
            });

            if (result?.success) {
                // Show success message
                MySwal.fire({
                    title: t('success', { ns: 'labels' }),
                    text: t('updateSuccess', { ns: 'labels' }) || 'Assegnazione aggiornata con successo',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });

                // Close modal and refresh search results
                closeEditModal();
                searchAssignments();
            } else {
                throw new Error(result?.error || t('updateError', { ns: 'labels' }) || 'Errore durante l\'aggiornamento');
            }
        } catch (error) {
            console.error("Error updating assignment:", error);
            MySwal.fire({
                title: t('error', { ns: 'labels' }),
                text: error.message || t('updateError', { ns: 'labels' }) || 'Errore durante l\'aggiornamento',
                icon: 'error'
            });
        } finally {
            setSaving(false);
        }
    };

    // Delete assignment
    const handleDeleteAssignment = async () => {
        // Show confirmation dialog
        const confirmResult = await MySwal.fire({
            title: t('confirmDelete', { ns: 'labels' }) || 'Conferma eliminazione',
            text: t('areYouSureToDelete', { ns: 'labels' }) || 'Sei sicuro di voler eliminare questa assegnazione? Questa azione non può essere annullata.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: t('delete', { ns: 'labels' }) || 'Elimina',
            cancelButtonText: t('cancel', { ns: 'labels' }) || 'Annulla'
        });

        if (!confirmResult.isConfirmed) {
            return;
        }

        try {
            setSaving(true);

            // Show loading modal
            MySwal.fire({
                title: t('deleting', { ns: 'labels' }) || 'Eliminazione in corso...',
                text: t('deleting', { ns: 'labels' }) || 'Eliminazione in corso...',
                allowOutsideClick: false,
                showConfirmButton: false,
                willOpen: () => {
                    Swal.showLoading();
                }
            });

            // Call the delete API endpoint
            const result = await api.sendMessage("darm/formAssignments/delete", {
                user: currentUser(),
                body: { id: editingAssignment.Id }
            });

            if (result?.success) {
                // Show success message
                MySwal.fire({
                    title: t('success', { ns: 'labels' }),
                    text: t('deleteSuccess', { ns: 'labels' }) || 'Assegnazione eliminata con successo',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });

                // Close modal and refresh search results
                closeEditModal();
                searchAssignments();
            } else {
                throw new Error(result?.error || t('deleteError', { ns: 'labels' }) || 'Errore durante l\'eliminazione');
            }
        } catch (error) {
            console.error("Error deleting assignment:", error);
            MySwal.fire({
                title: t('error', { ns: 'labels' }),
                text: error.message || t('deleteError', { ns: 'labels' }) || 'Errore durante l\'eliminazione',
                icon: 'error'
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <div className="page-titles">
                <ol className="breadcrumb"></ol>

            </div>

            <div className="container-fluid">
                <div className="row">
                    <div className="col-xl-12">
                        <div className="card">
                            <div className="card-header">
                                <h4 className="heading mb-0">
                                    {t('formAssignment', { ns: 'labels' })}
                                </h4>
                            </div>

                            <div className="card-body">
                                {loading ? (
                                    <div className="d-flex justify-content-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">{t('loading', { ns: 'labels' })}</span>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="alert alert-info">
                                            <i className="fa-solid fa-info-circle me-2"></i>
                                            {t('formAssignmentInfo', { ns: 'labels' })}
                                        </div>

                                        <ul className="nav nav-tabs" id="assign-tab" role="tablist">
                                            <li className="nav-item" role="presentation">
                                                <button className={`nav-link ${activeTab === 'user' ? 'active' : ''}`} id="user-tab" onClick={() => setActiveTab('user')} type="button" role="tab" aria-controls="user-tab-pane" aria-selected={activeTab === 'user'}>
                                                    {t('assignToUser', { ns: 'labels' })}
                                                </button>
                                            </li>
                                            <li className="nav-item" role="presentation">
                                                <button className={`nav-link ${activeTab === 'group' ? 'active' : ''}`} id="group-tab" onClick={() => setActiveTab('group')} type="button" role="tab" aria-controls="group-tab-pane" aria-selected={activeTab === 'group'}>
                                                    {t('assignToGroup', { ns: 'labels' })}
                                                </button>
                                            </li>
                                        </ul>

                                        <div className="tab-content pt-3" id="assign-tab-content">
                                            <div className={`tab-pane fade ${activeTab === 'user' ? 'show active' : ''}`} id="user-tab-pane" role="tabpanel" aria-labelledby="user-tab">
                                                <label className="form-label">
                                                    {t('selectUser', { ns: 'labels' })}
                                                    <span className="text-danger ms-1">*</span>
                                                </label>
                                                <Choices
                                                    voc={users}
                                                    value="Code"
                                                    text="Name"
                                                    className="inputStyle blackText bg-white"
                                                    selected={selectedUser ? [{ Code: selectedUser }] : []}
                                                    onChange={(e) => {
                                                        const selected = e.selected?.[0]?.Code || "";
                                                        const selectedUserData = users.find(user => user.Code === selected);
                                                        setSelectedUser(selected);
                                                        setSelectedUserObject(selectedUserData?.OriginalUser || null);
                                                    }}
                                                    placeholder={t('selectUser', { ns: 'labels' })}
                                                    dropDownParent="root"
                                                />
                                            </div>
                                            <div className={`tab-pane fade ${activeTab === 'group' ? 'show active' : ''}`} id="group-tab-pane" role="tabpanel" aria-labelledby="group-tab">
                                                <label className="form-label">
                                                    {t('selectGroup', { ns: 'labels' })}
                                                    <span className="text-danger ms-1">*</span>
                                                </label>
                                                <Choices
                                                    voc={groups}
                                                    value="Code"
                                                    text="Name"
                                                    className="inputStyle blackText bg-white"
                                                    selected={selectedGroup ? [{ Code: selectedGroup }] : []}
                                                    onChange={(e) => {
                                                        const selected = e.selected?.[0]?.Code || "";
                                                        const selectedGroupData = groups.find(group => group.Code === selected);
                                                        setSelectedGroup(selected);
                                                        setSelectedGroupObject(selectedGroupData?.OriginalGroup || null);
                                                    }}
                                                    placeholder={t('selectGroup', { ns: 'labels' })}
                                                    dropDownParent="root"
                                                />
                                            </div>
                                        </div>

                                        <div className="row g-3 mt-2">
                                            <div className="col-md-12">
                                                <label className="form-label">
                                                    {t('selectForm', { ns: 'labels' })}
                                                    <span className="text-danger ms-1">*</span>
                                                </label>
                                                <Choices
                                                    voc={formList}
                                                    value="Code"
                                                    text="Name"
                                                    className="inputStyle blackText bg-white"
                                                    selected={selectedForm ? [{ Code: selectedForm }] : []}
                                                    onChange={(e) => {
                                                        const selected = e.selected?.[0]?.Code || "";
                                                        setSelectedForm(selected);
                                                    }}
                                                    placeholder={t('selectForm', { ns: 'labels' })}
                                                    dropDownParent="root"
                                                />
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label">
                                                    {t('cycles', { ns: 'labels' }) || 'Cicli'}
                                                </label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    value={cycles}
                                                    onChange={(e) => setCycles(parseInt(e.target.value) || 1)}
                                                    min="1"
                                                    max="100"
                                                />
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label">
                                                    {t('expectedStartDate', { ns: 'labels' })}
                                                </label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    value={expectedCompilationStartDate}
                                                    onChange={(e) => setExpectedCompilationStartDate(e.target.value)}
                                                />
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label">
                                                    {t('expectedEndDate', { ns: 'labels' })}
                                                </label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    value={expectedCompilationEndDate}
                                                    onChange={(e) => setExpectedCompilationEndDate(e.target.value)}
                                                />
                                            </div>

                                            <div className="col-12">
                                                <div className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id="requiresValidation"
                                                        checked={requiresValidation}
                                                        onChange={(e) => setRequiresValidation(e.target.checked)}
                                                    />
                                                    <label className="form-check-label" htmlFor="requiresValidation">
                                                        {t('requiresValidation', { ns: 'labels' })}
                                                    </label>
                                                </div>
                                            </div>

                                            <div className="col-12 mt-4">
                                                <button
                                                    type="button"
                                                    className="btn btn-primary"
                                                    onClick={handleAssign}
                                                    disabled={saving}
                                                >
                                                    {saving ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                            {t('saving', { ns: 'labels' })}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="fa-solid fa-user-plus me-2"></i>
                                                            {t('assign', { ns: 'labels' })}
                                                        </>
                                                    )}
                                                </button>

                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sezione Ricerca Assegnazioni - Separata */}
                <div className="row">
                    <div className="col-xl-12">
                        <div className="card">
                            <div className="card-header">
                                <h4 className="heading mb-0">
                                    {t('searchAssignments', { ns: 'labels' }) || 'Ricerca assegnazioni'}
                                </h4>
                            </div>
                            <div className="card-body">
                                <div className="alert alert-info">
                                    <i className="fa-solid fa-info-circle me-2"></i>
                                    {t('searchAssignmentsInfo', { ns: 'labels' }) || 'Cerca e gestisci le assegnazioni esistenti utilizzando i filtri sottostanti.'}
                                </div>
                                
                                {/* Filtri di ricerca */}
                                <div className="row g-3 mb-4">
                                    <div className="col-md-4">
                                        <label className="form-label">
                                            {t('selectUser', { ns: 'labels' })}
                                        </label>
                                        <Choices
                                            voc={[{ Code: "", Name: t('allUsers', { ns: 'labels' }) || 'Tutti gli utenti' }, ...users]}
                                            value="Code"
                                            text="Name"
                                            className="inputStyle blackText bg-white"
                                            selected={searchFilters.userId ? [{ Code: searchFilters.userId }] : [{ Code: "" }]}
                                            onChange={(e) => {
                                                const selected = e.selected?.[0]?.Code || "";
                                                handleFilterChange('userId', selected);
                                            }}
                                            placeholder={t('selectUser', { ns: 'labels' })}
                                            dropDownParent="root"
                                        />
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label">
                                            {t('selectGroup', { ns: 'labels' })}
                                        </label>
                                        <Choices
                                            voc={[{ Code: "", Name: t('allGroups', { ns: 'labels' }) || 'Tutti i gruppi' }, ...groups]}
                                            value="Code"
                                            text="Name"
                                            className="inputStyle blackText bg-white"
                                            selected={searchFilters.groupId ? [{ Code: searchFilters.groupId }] : [{ Code: "" }]}
                                            onChange={(e) => {
                                                const selected = e.selected?.[0]?.Code || "";
                                                handleFilterChange('groupId', selected);
                                            }}
                                            placeholder={t('selectGroup', { ns: 'labels' })}
                                            dropDownParent="root"
                                        />
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label">
                                            {t('selectForm', { ns: 'labels' })}
                                        </label>
                                        <Choices
                                            voc={[{ Code: "", Name: t('allForms', { ns: 'labels' }) || 'Tutti i form' }, ...formList]}
                                            value="Code"
                                            text="Name"
                                            className="inputStyle blackText bg-white"
                                            selected={searchFilters.formId ? [{ Code: searchFilters.formId }] : [{ Code: "" }]}
                                            onChange={(e) => {
                                                const selected = e.selected?.[0]?.Code || "";
                                                handleFilterChange('formId', selected);
                                            }}
                                            placeholder={t('selectForm', { ns: 'labels' })}
                                            dropDownParent="root"
                                        />
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label">
                                            {t('requiresValidation', { ns: 'labels' })}
                                        </label>
                                        <select 
                                            className="form-control"
                                            value={searchFilters.requiresValidation === null ? "" : searchFilters.requiresValidation.toString()}
                                            onChange={(e) => {
                                                const value = e.target.value === "" ? null : e.target.value === "true";
                                                handleFilterChange('requiresValidation', value);
                                            }}
                                        >
                                            <option value="">{t('allValidationTypes', { ns: 'labels' }) || 'Tutti i tipi'}</option>
                                            <option value="true">{t('yes', { ns: 'labels' })}</option>
                                            <option value="false">{t('no', { ns: 'labels' })}</option>
                                        </select>
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label">
                                            {t('searchDateFrom', { ns: 'labels' }) || 'Data inizio da'}
                                        </label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={searchFilters.startDateFrom}
                                            onChange={(e) => handleFilterChange('startDateFrom', e.target.value)}
                                        />
                                    </div>

                                    <div className="col-md-4">
                                        <label className="form-label">
                                            {t('searchDateTo', { ns: 'labels' }) || 'Data fine entro'}
                                        </label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={searchFilters.endDateBefore}
                                            onChange={(e) => handleFilterChange('endDateBefore', e.target.value)}
                                        />
                                    </div>

                                    <div className="col-md-4 d-flex align-items-end">
                                        <div className="form-check me-3">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="includeInactive"
                                                checked={searchFilters.includeInactive}
                                                onChange={(e) => handleFilterChange('includeInactive', e.target.checked)}
                                            />
                                            <label className="form-check-label" htmlFor="includeInactive">
                                                {t('includeInactive', { ns: 'labels' }) || 'Includi inattivi'}
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                {/* Pulsanti azioni */}
                                <div className="row mb-4">
                                    <div className="col-12">
                                        <button
                                            type="button"
                                            className="btn btn-primary me-2"
                                            onClick={() => searchAssignments()}
                                            disabled={searchLoading}
                                        >
                                            {searchLoading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    {t('searching', { ns: 'labels' }) || 'Ricerca...'}
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fa-solid fa-search me-2"></i>
                                                    {t('search', { ns: 'labels' })}
                                                </>
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={handleClearFilters}
                                            disabled={searchLoading}
                                        >
                                            {t('clear', { ns: 'labels' })}
                                        </button>
                                    </div>
                                </div>

                                {/* Risultati ricerca */}
                                <div className="mt-4">
                                    {assignments.length === 0 ? (
                                        <div className="text-center py-4">
                                            <i className="fa-solid fa-info-circle fa-2x text-muted mb-3"></i>
                                            <h6 className="text-muted">{t('noAssignmentsFound', { ns: 'labels' }) || 'Nessuna assegnazione trovata'}</h6>
                                            <p className="text-muted mb-0">
                                                {t('useFiltersToSearch', { ns: 'labels' }) || 'Utilizza i filtri sopra per cercare le assegnazioni e clicca "Cerca".'}
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="card">
                                            <div className="card-body p-0">
                                                <div className="active-projects task-table">
                                                    <div className="tbl-caption">
                                                        <h4 className="heading mb-0 text-start">
                                                            {t('searchResults', { ns: 'labels' }) || 'Risultati ricerca'} 
                                                            {assignments.length > 0 && (
                                                                <span className="badge bg-primary ms-2">{assignments.length}</span>
                                                            )}
                                                        </h4>
                                                    </div>
                                                    <div className="table-responsive">
                                                        <table className="table table-striped table-sm">
                                                            <thead>
                                                                <tr>
                                                                    <th style={{ 
                                                                        wordWrap: 'break-word',
                                                                        whiteSpace: 'normal',
                                                                        maxWidth: '150px',
                                                                        minWidth: '120px',
                                                                        fontSize: '0.875rem'
                                                                    }}>
                                                                        {t('form', { ns: 'labels' })}
                                                                    </th>
                                                                    <th style={{ 
                                                                        wordWrap: 'break-word',
                                                                        whiteSpace: 'normal',
                                                                        maxWidth: '130px',
                                                                        minWidth: '100px',
                                                                        fontSize: '0.875rem'
                                                                    }}>
                                                                        {t('assignedTo', { ns: 'labels' }) || 'Assegnato a'}
                                                                    </th>
                                                                    <th style={{ 
                                                                        whiteSpace: 'nowrap',
                                                                        minWidth: '60px',
                                                                        fontSize: '0.875rem'
                                                                    }}>
                                                                        {t('assignmentType', { ns: 'labels' }) || 'Tipo'}
                                                                    </th>
                                                                    <th style={{ 
                                                                        whiteSpace: 'nowrap',
                                                                        minWidth: '80px',
                                                                        fontSize: '0.875rem'
                                                                    }}>
                                                                        {t('startDate', { ns: 'labels' })}
                                                                    </th>
                                                                    <th style={{ 
                                                                        whiteSpace: 'nowrap',
                                                                        minWidth: '80px',
                                                                        fontSize: '0.875rem'
                                                                    }}>
                                                                        {t('endDate', { ns: 'labels' })}
                                                                    </th>
                                                                    <th style={{ 
                                                                        whiteSpace: 'nowrap',
                                                                        minWidth: '50px',
                                                                        fontSize: '0.875rem',
                                                                        textAlign: 'center'
                                                                    }}>
                                                                        {t('cycles', { ns: 'labels' })}
                                                                    </th>
                                                                    <th style={{ 
                                                                        whiteSpace: 'nowrap',
                                                                        minWidth: '70px',
                                                                        fontSize: '0.875rem',
                                                                        textAlign: 'center'
                                                                    }}>
                                                                        {t('requiresValidation', { ns: 'labels' })}
                                                                    </th>
                                                                    <th style={{ 
                                                                        whiteSpace: 'nowrap',
                                                                        minWidth: '60px',
                                                                        fontSize: '0.875rem',
                                                                        textAlign: 'center'
                                                                    }}>
                                                                        {t('status', { ns: 'labels' }) || 'Stato'}
                                                                    </th>
                                                                    <th style={{ 
                                                                        whiteSpace: 'nowrap',
                                                                        minWidth: '80px',
                                                                        fontSize: '0.875rem'
                                                                    }}>
                                                                        {t('createdDate', { ns: 'labels' }) || 'Data creazione'}
                                                                    </th>
                                                                    <th style={{ 
                                                                        whiteSpace: 'nowrap',
                                                                        minWidth: '80px',
                                                                        fontSize: '0.875rem',
                                                                        textAlign: 'center'
                                                                    }}>
                                                                        {t('actions', { ns: 'labels' }) || 'Azioni'}
                                                                    </th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {assignments.map((assignment) => (
                                                                    <tr key={assignment.Id}>
                                                                        <td style={{
                                                                            wordWrap: 'break-word',
                                                                            wordBreak: 'break-word',
                                                                            whiteSpace: 'normal',
                                                                            maxWidth: '150px',
                                                                            fontSize: '0.875rem'
                                                                        }}>
                                                                            <strong>{getFormName(assignment.FormId)}</strong>
                                                                        </td>
                                                                        <td style={{
                                                                            wordWrap: 'break-word',
                                                                            wordBreak: 'break-word',
                                                                            whiteSpace: 'normal',
                                                                            maxWidth: '130px',
                                                                            fontSize: '0.875rem'
                                                                        }}>
                                                                            {assignment.UserId ? getUserName(assignment.UserId) : getGroupName(assignment.GroupId)}
                                                                        </td>
                                                                        <td style={{ 
                                                                            whiteSpace: 'nowrap',
                                                                            fontSize: '0.875rem'
                                                                        }}>
                                                                            {assignment.UserId ? t('user', { ns: 'labels' }) || 'Utente' : t('group', { ns: 'labels' }) || 'Gruppo'}
                                                                        </td>
                                                                        <td style={{ 
                                                                            whiteSpace: 'nowrap',
                                                                            fontSize: '0.875rem'
                                                                        }}>
                                                                            {formatDate(assignment.StartDate)}
                                                                        </td>
                                                                        <td style={{ 
                                                                            whiteSpace: 'nowrap',
                                                                            fontSize: '0.875rem'
                                                                        }}>
                                                                            {formatDate(assignment.EndDate)}
                                                                        </td>
                                                                        <td style={{ 
                                                                            whiteSpace: 'nowrap', 
                                                                            textAlign: 'center',
                                                                            fontSize: '0.875rem'
                                                                        }}>
                                                                            {assignment.Cycles || 1}
                                                                        </td>
                                                                        <td style={{ 
                                                                            whiteSpace: 'nowrap', 
                                                                            textAlign: 'center',
                                                                            fontSize: '0.875rem'
                                                                        }}>
                                                                            {assignment.ToValidate ? t('yes', { ns: 'labels' }) : t('no', { ns: 'labels' })}
                                                                        </td>
                                                                        <td style={{ 
                                                                            whiteSpace: 'nowrap', 
                                                                            textAlign: 'center',
                                                                            fontSize: '0.875rem'
                                                                        }}>
                                                                            {assignment.Status}
                                                                        </td>
                                                                        <td style={{ 
                                                                            whiteSpace: 'nowrap',
                                                                            fontSize: '0.875rem'
                                                                        }}>
                                                                            {formatDate(assignment.ItemCreated)}
                                                                        </td>
                                                                        <td style={{ 
                                                                            whiteSpace: 'nowrap',
                                                                            fontSize: '0.875rem',
                                                                            textAlign: 'center'
                                                                        }}>
                                                                            <a
                                                                                className="text-primary fs-13"
                                                                                title={t('edit', { ns: 'labels' }) || 'Modifica'}
                                                                                role="button"
                                                                                onClick={() => openEditModal(assignment)}
                                                                                style={{ 
                                                                                    display: 'inline-block',
                                                                                    padding: '2px 4px',
                                                                                    minWidth: '30px',
                                                                                    cursor: 'pointer'
                                                                                }}
                                                                            >
                                                                                <i className="fa-solid fa-edit fa-lg"></i>
                                                                            </a>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Edit Assignment Modal */}
            {showEditModal && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }} tabIndex="-1">
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {t('editAssignment', { ns: 'labels' }) || 'Modifica Assegnazione'}
                                </h5>
                                <button type="button" className="btn-close" onClick={closeEditModal}></button>
                            </div>
                            <div className="modal-body">
                                {editingAssignment && (
                                    <>
                                        <div className="alert alert-info">
                                            <i className="fa-solid fa-info-circle me-2"></i>
                                            {t('editAssignmentInfo', { ns: 'labels' }) || 'Modifica i dettagli dell\'assegnazione selezionata.'}
                                        </div>

                                        <ul className="nav nav-tabs" id="edit-assign-tab" role="tablist">
                                            <li className="nav-item" role="presentation">
                                                <button className={`nav-link ${editForm.activeTab === 'user' ? 'active' : ''}`} 
                                                        id="edit-user-tab" 
                                                        onClick={() => setEditForm({...editForm, activeTab: 'user'})} 
                                                        type="button" 
                                                        role="tab" 
                                                        aria-controls="edit-user-tab-pane" 
                                                        aria-selected={editForm.activeTab === 'user'}>
                                                    {t('assignToUser', { ns: 'labels' })}
                                                </button>
                                            </li>
                                            <li className="nav-item" role="presentation">
                                                <button className={`nav-link ${editForm.activeTab === 'group' ? 'active' : ''}`} 
                                                        id="edit-group-tab" 
                                                        onClick={() => setEditForm({...editForm, activeTab: 'group'})} 
                                                        type="button" 
                                                        role="tab" 
                                                        aria-controls="edit-group-tab-pane" 
                                                        aria-selected={editForm.activeTab === 'group'}>
                                                    {t('assignToGroup', { ns: 'labels' })}
                                                </button>
                                            </li>
                                        </ul>

                                        <div className="tab-content pt-3" id="edit-assign-tab-content">
                                            <div className={`tab-pane fade ${editForm.activeTab === 'user' ? 'show active' : ''}`} 
                                                 id="edit-user-tab-pane" 
                                                 role="tabpanel" 
                                                 aria-labelledby="edit-user-tab">
                                                <label className="form-label">
                                                    {t('selectUser', { ns: 'labels' })}
                                                    <span className="text-danger ms-1">*</span>
                                                </label>
                                                <Choices
                                                    voc={users}
                                                    value="Code"
                                                    text="Name"
                                                    className="inputStyle blackText bg-white"
                                                    selected={editForm.selectedUser ? [{ Code: editForm.selectedUser }] : []}
                                                    onChange={(e) => {
                                                        const selected = e.selected?.[0]?.Code || "";
                                                        setEditForm({...editForm, selectedUser: selected});
                                                    }}
                                                    placeholder={t('selectUser', { ns: 'labels' })}
                                                />
                                            </div>
                                            <div className={`tab-pane fade ${editForm.activeTab === 'group' ? 'show active' : ''}`} 
                                                 id="edit-group-tab-pane" 
                                                 role="tabpanel" 
                                                 aria-labelledby="edit-group-tab">
                                                <label className="form-label">
                                                    {t('selectGroup', { ns: 'labels' })}
                                                    <span className="text-danger ms-1">*</span>
                                                </label>
                                                <Choices
                                                    voc={groups}
                                                    value="Code"
                                                    text="Name"
                                                    className="inputStyle blackText bg-white"
                                                    selected={editForm.selectedGroup ? [{ Code: editForm.selectedGroup }] : []}
                                                    onChange={(e) => {
                                                        const selected = e.selected?.[0]?.Code || "";
                                                        setEditForm({...editForm, selectedGroup: selected});
                                                    }}
                                                    placeholder={t('selectGroup', { ns: 'labels' })}
                                                />
                                            </div>
                                        </div>

                                        <div className="row g-3 mt-2">
                                            <div className="col-md-12">
                                                <label className="form-label">
                                                    {t('selectForm', { ns: 'labels' })}
                                                    <span className="text-danger ms-1">*</span>
                                                </label>
                                                <Choices
                                                    voc={formList}
                                                    value="Code"
                                                    text="Name"
                                                    className="inputStyle blackText bg-white"
                                                    selected={editForm.selectedForm ? [{ Code: editForm.selectedForm }] : []}
                                                    onChange={(e) => {
                                                        const selected = e.selected?.[0]?.Code || "";
                                                        setEditForm({...editForm, selectedForm: selected});
                                                    }}
                                                    placeholder={t('selectForm', { ns: 'labels' })}
                                                />
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label">
                                                    {t('cycles', { ns: 'labels' }) || 'Cicli'}
                                                </label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    value={editForm.cycles}
                                                    onChange={(e) => setEditForm({...editForm, cycles: parseInt(e.target.value) || 1})}
                                                    min="1"
                                                    max="100"
                                                />
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label">
                                                    {t('expectedStartDate', { ns: 'labels' })}
                                                </label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    value={editForm.expectedCompilationStartDate}
                                                    onChange={(e) => setEditForm({...editForm, expectedCompilationStartDate: e.target.value})}
                                                />
                                            </div>

                                            <div className="col-md-4">
                                                <label className="form-label">
                                                    {t('expectedEndDate', { ns: 'labels' })}
                                                </label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    value={editForm.expectedCompilationEndDate}
                                                    onChange={(e) => setEditForm({...editForm, expectedCompilationEndDate: e.target.value})}
                                                />
                                            </div>

                                            <div className="col-12">
                                                <div className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id="editRequiresValidation"
                                                        checked={editForm.requiresValidation}
                                                        onChange={(e) => setEditForm({...editForm, requiresValidation: e.target.checked})}
                                                    />
                                                    <label className="form-check-label" htmlFor="editRequiresValidation">
                                                        {t('requiresValidation', { ns: 'labels' })}
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={closeEditModal}
                                    disabled={saving}
                                >
                                    {t('cancel', { ns: 'labels' }) || 'Annulla'}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger me-2"
                                    onClick={handleDeleteAssignment}
                                    disabled={saving}
                                >
                                    {saving ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            {t('deleting', { ns: 'labels' }) || 'Eliminazione...'}
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa-solid fa-trash me-2"></i>
                                            {t('delete', { ns: 'labels' }) || 'Elimina'}
                                        </>
                                    )}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleSaveChanges}
                                    disabled={saving}
                                >
                                    {saving ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            {t('saving', { ns: 'labels' })}
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa-solid fa-save me-2"></i>
                                            {t('saveChanges', { ns: 'labels' }) || 'Salva modifiche'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export { Form_Assignment_Screen };
